import get from 'lodash/get';
import recognitionService from 'app/services/recognition';
import { endLoading, startLoading } from 'app/app-dux/app-dux';
import { getSelectedPeople, setPersonPoints } from './people-dux';
import { CLEAR_RECOGNITION } from 'app/app-dux/recognition-dux';
import { USER_STORAGE_KEY } from '../../constants';

/*******************************************************************************
 * Initial state
 */
const initialState = {
	programs: [],
	selectedProgram: {},
	selectedBehavior: {},
	differentPoints: false,
	awardValue: '',
	budgetError: null
};

/*******************************************************************************
 * Non-async action types
 */
export const SET_SELECTED_PROGRAM = 'SET_SELECTED_PROGRAM';
export const SET_PROGRAMS_AND_REWARDS = 'SET_PROGRAMS_AND_REWARDS';
export const SET_SELECTED_BEHAVIOR = 'SET_SELECTED_BEHAVIOR';
export const SET_DIFFERENT_POINTS = 'SET_DIFFERENT_POINTS';
export const SET_AWARD_VALUE = 'SET_AWARD_VALUE';
export const SET_BUDGET_ERROR = 'SET_BUDGET_ERROR';

/*******************************************************************************
 * Reducer
 */
export default function programReducer( state = initialState, action ) {
	switch ( action.type ) {
		case SET_PROGRAMS_AND_REWARDS: {
			return {
				...state,
				programs: action.rewardChoicePrograms
			};
		}
		case SET_SELECTED_PROGRAM: {
			return {
				...state,
				selectedProgram: action.selectedProgram
			};
		}
		case SET_SELECTED_BEHAVIOR: {
			return {
				...state,
				selectedBehavior: action.selectedBehavior
			};
		}
		case SET_DIFFERENT_POINTS: {
			return {
				...state,
				differentPoints: action.differentPoints
			};
		}
		case SET_AWARD_VALUE: {
			return {
				...state,
				awardValue: action.awardValue
			};
		}
		case SET_BUDGET_ERROR: {
			return {
				...state,
				budgetError: action.budgetError
			};
		}
		case CLEAR_RECOGNITION: {
			return {
				...state,
				selectedProgram: {},
				selectedBehavior: {},
				differentPoints: false,
				awardValue: 0
			};
		}
		default:
			return state;
	}
}

/*******************************************************************************
 * Action creators
 */
export const setProgramsAndRewards = rewardChoicePrograms => ( { type: SET_PROGRAMS_AND_REWARDS, rewardChoicePrograms } );
export const setSelectedProgram = ( selectedProgram ) => dispatch => { return dispatch( { type: SET_SELECTED_PROGRAM, selectedProgram } ); };
export const setSelectedBehavior = ( selectedBehavior ) => dispatch => { return dispatch( { type: SET_SELECTED_BEHAVIOR, selectedBehavior } ); };
export const setDifferentPoints = ( differentPoints ) => dispatch => {
	return dispatch( { type: SET_DIFFERENT_POINTS, differentPoints } );
};
export const setAwardValue = ( awardValue ) => ( { type: SET_AWARD_VALUE, awardValue } );
export const setBudgetError = ( budgetError ) => ( { type: SET_BUDGET_ERROR, budgetError } );
export const searchProgramsAndRewardChoices = () => async ( dispatch ) => {
	dispatch( startLoading() );
	try {
		let user = localStorage.getItem( USER_STORAGE_KEY );
		user = user ? JSON.parse( user ) : {};

		const response = await recognitionService.searchProgramsAndRewardChoices( {
			contentFetchingLocale: user?.claims?.locale ?? 'en-US'
		} );
		let rewardChoicePrograms = [];
		if ( response && response.rewardChoices.length > 0 ) {
			response.rewardChoices.map( choice => {
				const program = choice.program;
				if ( choice.rewardStack && choice.rewardStack.rewards && choice.rewardStack.rewards.length > 0 ) {
					const hasPoints = choice.rewardStack.rewards.filter( reward => {return reward.type === 'POINTS';} );
					program.hasPoints = hasPoints.length > 0;
					if ( hasPoints.length > 0 ) {
						program.minAward = hasPoints[ 0 ].reward.minValue;
						program.maxAward = hasPoints[ 0 ].reward.maxValue;
						program.hasPoints = true;
						program.pointsRequired = choice.requiredRewardTypes.includes( 'POINTS' );
						program.isFixed = hasPoints[ 0 ].reward.inputType === 'FIXED';
					}
					const ecards = choice.rewardStack.rewards.filter( reward => {return reward.type === 'ECARD';} );
					program.ecards = ecards;
					program.ecardRequired = choice.requiredRewardTypes.includes( 'ECARD' );
				}
				program.rewardChoiceId = choice.id;
				program.rewardChoiceName = choice.name;
				if ( choice.activity && choice.activity.activityConfig && choice.activity.activityConfig.visibility ) {
					program.visibility = choice.activity.activityConfig.visibility;
				}
				if ( choice.activity && choice.activity.activityConfig && choice.activity.activityConfig.behaviors ) {
					program.behaviors = choice.activity.activityConfig.behaviors;
					program.isBehaviorRequired = choice.activity.activityConfig.isBehaviorRequired;
				}
				if ( choice.budgets && choice.budgets.length ) {
					choice.budgets.map( budget => {
						const { endDate } = budget;
						if ( endDate ) {
							const numOfDays = ( new Date( endDate ).getTime() - new Date().getTime() ) / ( 1000 * 60 * 60 * 24 );
							const niceNumOfDays = Math.round( numOfDays );
							return budget.endDays = niceNumOfDays;
						}
					} );
					program.budgets = choice.budgets;

					const programBudgets = choice.budgets.filter( budget => { return budget.foreignIds && budget.foreignIds.length > 0 && budget.foreignIds.filter( foreign => { return foreign.foreignIdType === 'PROGRAM_ID';} );} );

					if (
						programBudgets &&
						programBudgets.length === 1 &&
						programBudgets[ 0 ].foreignIds.filter( foreign => { return foreign.foreignIdType === 'PROGRAM_ID' && foreign.foreignId === program.id; } )
					) {
						program.pointsDisplay = programBudgets[ 0 ].convertedCurrentValue;
						program.endDays = programBudgets[ 0 ].endDays;
					} else if ( programBudgets && programBudgets.length > 1 ) {
						const maxBudget = programBudgets.sort( ( a, b ) => b.convertedCurrentValue - a.convertedCurrentValue )[ 0 ];
						program.pointsDisplay = maxBudget.convertedCurrentValue;
						program.endDays = maxBudget.endDays;
					} else {
						const maxBudget = choice.budgets.sort( ( a, b ) => b.convertedCurrentValue - a.convertedCurrentValue )[ 0 ];
						program.pointsDisplay = maxBudget.convertedCurrentValue;
						program.endDays = maxBudget.endDays;
					}
				}
				rewardChoicePrograms.push( program );
			} );
			dispatch( setProgramsAndRewards( rewardChoicePrograms ) );
		}
	} catch ( error ) {
		console.error( error );
	} finally {
		dispatch( endLoading() );
	}
};

export const setPointsForPerson = ( personId, value ) => ( dispatch, getState ) => {
	const state = getState();
	const selectedPeople = state.people.selectedPeople;
	if ( personId === 'all' )
	{
		dispatch( setAwardValue( value ) );
	}
	for ( let i = 0; i < selectedPeople.length; i++ ) {
		if ( personId === 'all' )
		{
			dispatch( setPersonPoints( selectedPeople[ i ].id, value ) );
		}
		else {
			if ( selectedPeople[ i ].id === personId )
			{
				dispatch( setPersonPoints( personId, value ) );
				break;
			}
		}
	}
};

// selectors
export const getPrograms = state => get( state, 'program.programs', [] );
export const getSelectedProgram = state => get( state, 'program.selectedProgram', {} );
export const getBehaviors = state => get( getSelectedProgram( state ), 'behaviors', [] );
export const getSelectedBehavior = state => get( state, 'program.selectedBehavior', {} );
export const getEcards = state => get( getSelectedProgram( state ), 'ecards', [] );
export const getOriginalBudget = state => {
	const budget = get( getSelectedProgram( state ), 'budgets[0]', {} );
	const { convertedCurrentValue } = budget;
	return convertedCurrentValue;
};
export const getBudgetError = state => get( state, 'program.budgetError' );
export const getAwardValue = state => get( state, 'program.awardValue', '' );
export const getTotalAward = state => {
	const selectedPeople = getSelectedPeople( state );
	let totalAward = 0;
	for ( let i = 0; i < selectedPeople.length; i++ )
	{
		totalAward = totalAward + selectedPeople[ i ].awardValue || 0;
	}
	return totalAward;
};

export const getRemainingBudget = state => {
	const budget = get( getSelectedProgram( state ), 'budgets[0]', {} );
	const totalAward = getTotalAward( state );
	const { convertedCurrentValue } = budget;
	let remainingBudget = 0;
	if ( convertedCurrentValue )
	{
		remainingBudget = convertedCurrentValue - totalAward;
	}
	return remainingBudget;
};
export const getRemainingBudgetPercent = state => {
	const budget = get( getSelectedProgram( state ), 'budgets[0]', {} );
	const { convertedInitialValue } = budget;
	const remainingBudget = getRemainingBudget( state );
	let percent = 0;
	if ( remainingBudget && convertedInitialValue )
	{
		percent = ( parseInt( remainingBudget ) / parseInt( convertedInitialValue ) * 100 );
	}
	return percent;
};